/*
=========================================================
* Material Dashboard 2 PRO React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect } from "react";

// @mui material components
import Grid from "@mui/material/Grid";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

import Swal from "sweetalert2";

// Material Dashboard 2 PRO React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

// NewEvent page components
import EventImage from "./components/EventImage";
import FormFields from "./components/FormFields";
import EventDescription from "./components/Description";
import EventImageCover from "./components/EventImageCover";
import { addNewEventApi } from "api/events";
import { addNewEventImgsApi } from "api/events";
import { deleteEventApi } from "api/events";
import { useNavigate } from "react-router-dom";
import { CircularProgress } from "@mui/material";
import EventImageSieges from "./components/EventImageSieges";
import EventImages from "./components/EventImages";

function NewEvent() {
  const navigate = useNavigate();

  const [formValue, setFormValue] = useState({
    eventMainImg: null,
    eventCoverImg: null,
    eventSiegesImg: null,
    otherEventImgs: null,
    description: "",
    eventInfo: {},
  });
  const [newEventLoading, setNewEventLoading] = useState(false);

  // useEffect(() => {
  //   console.log("formValue changed:", formValue);
  // }, [formValue]);

  const handleEventMainImg = async (value) => {
    try {
      setFormValue((prevFormValue) => ({
        ...prevFormValue,
        eventMainImg: value,
      }));
    } catch (err) {
      console.log(err);
    }
  };

  const handleEventCoverImg = async (value) => {
    try {
      setFormValue((prevFormValue) => ({
        ...prevFormValue,
        eventCoverImg: value,
      }));
    } catch (err) {
      console.log(err);
    }
  };

  const handleEventSiegeImg = async (value) => {
    try {
      setFormValue((prevFormValue) => ({
        ...prevFormValue,
        eventSiegesImg: value,
      }));
    } catch (err) {
      console.log(err);
    }
  };

  const handleEventImgs = async (value) => {
    try {
      setFormValue((prevFormValue) => ({
        ...prevFormValue,
        otherEventImgs: value,
      }));
    } catch (err) {
      console.log(err);
    }
  };

  const handleAddNewEvent = async () => {
    try {
      setNewEventLoading(true);

      const eventImgObject = {
        eventMainImg: formValue.eventMainImg,
        eventCoverImg: formValue.eventCoverImg,
        eventSiegesImg: formValue.eventSiegesImg,
        otherEventImgs: formValue.otherEventImgs,
      };

      const eventObject = {
        eventTitle: formValue?.eventInfo?.eventTitle || "",
        promoVideo: formValue?.eventInfo?.promoVideo || "",
        address: formValue?.eventInfo?.address || "",
        description: formValue?.description || "",
        eventPlace: formValue?.eventInfo?.eventPlace,
        eventCategory:
          formValue?.eventInfo?.eventCategory?.length > 0
            ? formValue?.eventInfo?.eventCategory.map((el) => el._id)
            : [],
        eventSiege: formValue?.eventInfo?.eventSiege,
        eventStand: formValue?.eventInfo?.eventStand,
        socialLink: formValue?.eventInfo?.socialLink,
        onlineReservation: formValue?.eventInfo?.onlineReservation,
        isSelectetivePlaces: formValue?.eventInfo?.isSelectetivePlaces,
        seatIoId: formValue?.eventInfo?.seatIoId,
        isSoldOut: formValue?.eventInfo?.isSoldOut,
        isCinema: formValue?.eventInfo?.isCinema,
        isCirque: formValue?.eventInfo?.isCirque,
        city: formValue?.eventInfo?.city || "",
      };

      if ((formValue?.eventInfo?.isCinema || formValue?.eventInfo?.isCirque) && formValue?.eventInfo?.eventDateList?.length > 0) {
        // eventDateList is already processed to ISO strings by buildEventDateList() in FormFields
        // Just compute the aggregate dates from the already-converted ISO strings
        let earliestStartDate = null;
        let latestEndDate = null;
        let earliestEventDate = null;
        let earliestCloseDoorDate = null;

        const eventDataListArray = formValue.eventInfo.eventDateList;

        eventDataListArray.forEach((showtime) => {
          if (showtime.startDate) {
            const currentDate = new Date(showtime.startDate);
            if (!earliestStartDate || currentDate < earliestStartDate)
              earliestStartDate = currentDate;
          }
          if (showtime.endDate) {
            const currentDate = new Date(showtime.endDate);
            if (!latestEndDate || currentDate > latestEndDate)
              latestEndDate = currentDate;
          }
          if (showtime.eventDate) {
            const currentDate = new Date(showtime.eventDate);
            if (!earliestEventDate || currentDate < earliestEventDate)
              earliestEventDate = currentDate;
          }
          if (showtime.eventCloseDoorDate) {
            const currentDate = new Date(showtime.eventCloseDoorDate);
            if (!earliestCloseDoorDate || currentDate < earliestCloseDoorDate)
              earliestCloseDoorDate = currentDate;
          }
        });

        eventObject.startDate = earliestStartDate ? earliestStartDate.toISOString() : null;
        eventObject.endDate = latestEndDate ? latestEndDate.toISOString() : null;
        eventObject.eventDate = earliestEventDate ? earliestEventDate.toISOString() : null;
        eventObject.eventCloseDoorDate = earliestCloseDoorDate ? earliestCloseDoorDate.toISOString() : null;
        eventObject.eventDateList = eventDataListArray;

        // eventObject.eventDateList = formValue?.eventInfo?.eventDateList;
        // eventObject.eventDateList = formValue?.eventInfo?.eventDateList.map((showtime) => ({
        //   ...showtime,
        //   // prices: showtime.prices.map(({ isSoldOut, ...rest }) => rest), // Remove isSoldOut from prices
        //   prices: showtime.prices, // Remove isSoldOut from prices
        // }));
      }
      else {
        const startDate = formValue?.eventInfo?.startDate;
        const startTime = formValue?.eventInfo?.startTime;
        if (startDate) {
          const [year, month, day] = startDate.split("-").map(Number);
          const [hour, minute] = startTime ? startTime.split(":").map(Number) : [0, 0];
          const dateObjUtc = new Date(Date.UTC(year, month - 1, day, hour, minute));
          eventObject.startDate = dateObjUtc.toISOString();
        } else {
          eventObject.startDate = null;
        }

        const endDate = formValue?.eventInfo?.endDate;
        const endTime = formValue?.eventInfo?.endTime;
        if (endDate) {
          const [year, month, day] = endDate.split("-").map(Number);
          const [hour, minute] = endTime ? endTime.split(":").map(Number) : [0, 0];
          const dateObjUtc = new Date(Date.UTC(year, month - 1, day, hour, minute));
          eventObject.endDate = dateObjUtc.toISOString();
        } else {
          eventObject.endDate = null;
        }

        const eventDate = formValue?.eventInfo?.eventDate;
        const eventTime = formValue?.eventInfo?.eventTime;
        if (eventDate) {
          const [year, month, day] = eventDate.split("-").map(Number);
          const [hour, minute] = eventTime ? eventTime.split(":").map(Number) : [0, 0];
          const dateObjUtc = new Date(Date.UTC(year, month - 1, day, hour, minute));
          eventObject.eventDate = dateObjUtc.toISOString();
        } else {
          eventObject.eventDate = null;
        }

        const eventDoorClosingDate = formValue?.eventInfo?.eventDoorClosingDate;
        const eventDoorClosingTime = formValue?.eventInfo?.eventDoorClosingTime;
        if (eventDoorClosingDate) {
          const [year, month, day] = eventDoorClosingDate.split("-").map(Number);
          const [hour, minute] = eventDoorClosingTime
            ? eventDoorClosingTime.split(":").map(Number)
            : [0, 0];
          const dateObjUtc = new Date(Date.UTC(year, month - 1, day, hour, minute));
          eventObject.eventCloseDoorDate = dateObjUtc.toISOString();
        } else {
          eventObject.eventCloseDoorDate = null;
        }

        eventObject.eventDateList = [];
      }

      const addNewEventApiRes = await addNewEventApi(eventObject);

      try {
        await addNewEventImgsApi(eventImgObject, addNewEventApiRes._id);
      } catch (imgErr) {
        // Image upload failed — roll back the event to avoid orphaned records
        try {
          await deleteEventApi(addNewEventApiRes._id);
        } catch (deleteErr) {
          console.error("Rollback failed:", deleteErr);
        }
        throw imgErr;
      }

      Swal.fire({
        title: "Ajoutée",
        text: "",
        icon: "success",
      });

      navigate("/evenements/voir");
    } catch (err) {
      Swal.fire({
        title: err.response?.data?.error || "Une erreur est survenue",
        text: "",
        icon: "error",
      });
    } finally {
      setNewEventLoading(false);
    }
  };

  const handleDescriptionValue = (value) => {
    try {
      if (formValue.description !== value) {
        // Only update state if the value is different
        setFormValue((prevFormValue) => ({
          ...prevFormValue,
          description: value,
        }));
      }
    } catch (err) {
      console.log(err);
    }
  };

  const handleGetFormValues = (value) => {
    try {
      setFormValue((prevFormValue) => ({
        ...prevFormValue,
        eventInfo: value,
      }));
    } catch (err) {
      console.log(err);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox my={3}>
        <MDBox mb={6}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} lg={6}>
              <MDTypography variant="h4" fontWeight="medium">
                Ajouter un événement
              </MDTypography>
              <MDBox mt={1} mb={2}>
                <MDTypography variant="body2" color="text"></MDTypography>
              </MDBox>
            </Grid>
            <Grid item xs={12} lg={6}>
              <MDBox display="flex" justifyContent="flex-end">
                {newEventLoading ? (
                  <CircularProgress color="info" size={30} />
                ) : (
                  <MDButton variant="gradient" color="info" onClick={handleAddNewEvent}>
                    Ajouter
                  </MDButton>
                )}
              </MDBox>
            </Grid>
          </Grid>
        </MDBox>
        <Grid container spacing={3}>
          <Grid item xs={12} lg={4} mb={3}>
            <EventImage imgValue={handleEventMainImg} />
          </Grid>
          <Grid item xs={12} lg={8}>
            <EventImageCover imgCoverValue={handleEventCoverImg} />
          </Grid>
          <Grid item xs={12} lg={6} mt={5}>
            <EventImageSieges imgSiegeValue={handleEventSiegeImg} />
          </Grid>
          <Grid item xs={12} lg={6} mt={5}>
            <EventImages imgValues={handleEventImgs} />
          </Grid>
          <Grid item xs={12}>
            <EventDescription descriptionValue={handleDescriptionValue} />
          </Grid>
          <Grid item xs={12}>
            <FormFields formValues={handleGetFormValues} />
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default NewEvent;
